'use client';

import React, { useState, useEffect } from 'react';

import styled from 'styled-components';

import { Format } from '@/lib/dateTime/formatDate';
import { log } from '@/utils/logger';

const Time = styled.time``;

/**
 * RelativeDate component that avoids hydration mismatches by using a consistent format during SSR and initial client render
 */
export default function RelativeDate({ date: dateString, dateOnly = false, locale }) {
  const [isClient, setIsClient] = useState(false);

  // Detect client-side rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  const memoizedData = React.useMemo(() => {
    if (!dateString) {
      return {
        relativeDate: '',
        dateIsoString: '',
        absoluteDate: '',
      };
    }

    const date = new Format(dateString);
    const relativeDate = date.in(locale).toRelativeTime();
    const dateIsoString = date.toIsoString();
    const timeWithoutAbout = relativeDate.replace('about ', ''); // @todo refactor

    // Use toString() which is available on your Format class
    const absoluteDate = date.in(locale).toString();

    return {
      relativeDate: timeWithoutAbout,
      dateIsoString,
      absoluteDate,
    };
  }, [dateString, locale]);

  const { relativeDate, dateIsoString, absoluteDate } = memoizedData;

  if (!dateString) {
    log.error('RelativeDate: date is missing');
    return null;
  }

  // Use absolute date format during SSR and first render on client to avoid hydration mismatch
  if (!isClient) {
    if (dateOnly) {
      return <Time dateTime={dateIsoString}>{absoluteDate}</Time>;
    }
    return <Time dateTime={dateIsoString}>Last updated {absoluteDate}</Time>;
  }

  // After client-side hydration, use the relative date
  if (dateOnly) {
    return <Time dateTime={dateIsoString}>{relativeDate}</Time>;
  }

  return <Time dateTime={dateIsoString}>Last updated {relativeDate}</Time>;
}
